import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import Spinner from '@/components/ui/Spinner';
import { useClient } from '@/contexts/ClientContext';
import { useAuditEventsPage, useAuditFacets, AUDIT_PAGE_SIZE } from '@/hooks/useAuditEvents';
import { auditEventsService } from '@/services/auditEvents';
import type { AuditEventEnvelope, AuditEventFilters } from '@/types';
import { actorFacetKey, actorFacetName, actorKeyFallbackLabel, categoryMeta, eventTypeLabel, statusLabel } from './auditMeta';
import {
  TIME_PRESETS,
  browserTimeZone,
  csvIsTruncated,
  customRangeStatus,
  facetSummary,
  initialAuditState,
  isStatusView,
  resolveRange,
  toggleEventTypeSelection,
  toggleStatusSelection,
  writeAuditUrlState,
  type AuditPreset,
  type AuditUrlState,
  type RollingPreset,
} from './auditState';
import { groupBursts } from './auditBursts';
import AuditDistributionBar from './AuditDistributionBar';
import AuditFilterBar, { type FilterChip } from './AuditFilterBar';
import AuditEventRow from './AuditEventRow';
import AuditBurstRow from './AuditBurstRow';
import AuditDetailDrawer from './AuditDetailDrawer';

// Minification-proof marker string — grepped in the deployed bundle to prove this shipped.
const AUDIT_EXPLORER_MARKER = 'audit-explorer-v2';

interface AuditExplorerProps {
  /** When provided, the console is scoped to a single client (client-scope usage). */
  clientId?: string | null;
  clientName?: string | null;
  /** 'client' = single-client; 'system' = full enterprise console (all clients per RBAC). */
  scope: 'client' | 'system';
  onClose?: () => void;
  /**
   * Initialize from, and mirror the console state into, the page URL (`?audit=1&…`).
   * Only the Header-hosted console sets this; pass false while it is closing.
   */
  urlSync?: boolean;
}

function readSearch(): string {
  try {
    return window.location.search;
  } catch {
    return '';
  }
}

function shortId(id: string): string {
  return id.length > 8 ? `…${id.slice(-6)}` : id;
}

const AuditExplorer: React.FC<AuditExplorerProps> = ({ clientId, clientName, scope, onClose, urlSync = false }) => {
  const { availableClients } = useClient();

  // Initial state: from the URL when `audit=1` is present, else the default view
  // (last 24 hours of status changes, all clients, all people).
  const [init] = useState<AuditUrlState>(() => initialAuditState(urlSync ? readSearch() : ''));

  // --- Date range ------------------------------------------------------------------------
  const [preset, setPreset] = useState<AuditPreset>(init.range);
  /** The last non-custom preset — what stays applied while a custom range is incomplete. */
  const [lastRolling, setLastRolling] = useState<RollingPreset>(init.range === 'custom' ? '1d' : init.range);
  const [customFrom, setCustomFrom] = useState(init.from);
  const [customTo, setCustomTo] = useState(init.to);
  /** The last COMPLETE custom pair; only this is ever sent to the server. */
  const [appliedCustom, setAppliedCustom] = useState<{ from: string; to: string } | null>(() =>
    customRangeStatus(init.from, init.to) === 'ok' ? { from: init.from, to: init.to } : null
  );
  /** Bumped by Refresh to re-anchor a rolling window on "now" (read by the filters memo). */
  const [refreshedAt, setRefreshedAt] = useState(0);

  // --- Filters -----------------------------------------------------------------------------
  const [qInput, setQInput] = useState(init.q);
  const [debouncedQ, setDebouncedQ] = useState(init.q);
  const [eventTypes, setEventTypes] = useState<string[]>(init.eventTypes);
  const [statuses, setStatuses] = useState<string[]>(init.statuses);
  const [actor, setActor] = useState<string | null>(init.actor);
  const [clientFilter, setClientFilter] = useState<string | null>(init.clientId);
  const [categories, setCategories] = useState<string[]>(init.categories);
  const [ip, setIp] = useState('');
  const [depositId, setDepositId] = useState<string | null>(init.depositId);
  const [depositLabel, setDepositLabel] = useState<string | null>(null);

  // --- UI ------------------------------------------------------------------------------------
  const [selected, setSelected] = useState<AuditEventEnvelope | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [expandedBursts, setExpandedBursts] = useState<Set<string>>(() => new Set());
  // Keyset pagination: a stack of page cursors (index 0 = first page = null cursor).
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  // Debounce the free-text search into the query.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(qInput.trim()), 350);
    return () => clearTimeout(t);
  }, [qInput]);

  // Auto-focus the search when the console opens.
  useEffect(() => {
    const t = setTimeout(() => searchRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);

  // Esc closes the detail drawer first (before the modal itself).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selected) {
        e.stopPropagation();
        setSelected(null);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [selected]);

  const enabled = scope === 'system' ? true : !!clientId;

  const filters: AuditEventFilters = useMemo(() => {
    // Rolling presets are "now − N" (minute-rounded, open-ended); `refreshedAt` only ever moves
    // "now" forward, so a Refresh click re-rolls the window.
    const range = resolveRange({ preset, lastRolling, appliedCustom }, new Date(Math.max(Date.now(), refreshedAt)));
    return {
      clientId: (scope === 'system' ? clientFilter : clientId) ?? undefined,
      from: range.from,
      to: range.to,
      category: categories.length ? categories : undefined,
      eventType: eventTypes.length ? eventTypes : undefined,
      status: statuses.length ? statuses : undefined,
      actor: actor ?? undefined,
      entityType: depositId ? 'deposit' : undefined,
      entityId: depositId ?? undefined,
      ip: ip.trim() || undefined,
      q: debouncedQ || undefined,
    };
  }, [
    clientId,
    scope,
    preset,
    lastRolling,
    appliedCustom,
    refreshedAt,
    clientFilter,
    categories,
    eventTypes,
    statuses,
    actor,
    depositId,
    ip,
    debouncedQ,
  ]);

  // Mirror the console state into the page URL (history.replaceState, unrelated params kept).
  const urlState: AuditUrlState = useMemo(
    () => ({
      range: preset,
      from: customFrom,
      to: customTo,
      eventTypes,
      statuses,
      actor,
      clientId: scope === 'system' ? clientFilter : null,
      categories,
      q: debouncedQ,
      depositId,
    }),
    [preset, customFrom, customTo, eventTypes, statuses, actor, scope, clientFilter, categories, debouncedQ, depositId]
  );

  useEffect(() => {
    if (urlSync) writeAuditUrlState(urlState);
  }, [urlSync, urlState]);

  // Any filter change resets paging back to the first page.
  useEffect(() => {
    setCursorStack([null]);
    setPageIndex(0);
  }, [filters]);

  const currentCursor = cursorStack[pageIndex] ?? null;
  const pageQuery = useAuditEventsPage(filters, currentCursor, enabled);
  const facetsQuery = useAuditFacets(filters, enabled);
  const facets = facetsQuery.data;
  const events = useMemo(() => pageQuery.data?.data ?? [], [pageQuery.data]);
  const listItems = useMemo(() => groupBursts(events), [events]);
  const nextCursor = pageQuery.data?.nextCursor ?? null;

  const statusView = isStatusView(eventTypes, statuses);
  const summary = facetSummary(facets, statusView);

  const activeFilterCount =
    categories.length +
    (eventTypes.length ? 1 : 0) +
    (statuses.length ? 1 : 0) +
    (actor ? 1 : 0) +
    (ip.trim() ? 1 : 0) +
    (scope === 'system' && clientFilter ? 1 : 0) +
    (debouncedQ ? 1 : 0) +
    (depositId ? 1 : 0);

  const total = facets?.total ?? null;
  const errStatus = (pageQuery.error as { response?: { status?: number } } | null)?.response?.status;
  const unauthorized = errStatus === 403 || errStatus === 401;

  // Pagination derived state (total is unknown until the facets load).
  const totalPages = total == null ? null : Math.max(1, Math.ceil(total / AUDIT_PAGE_SIZE));
  const hasPrev = pageIndex > 0;
  const hasNext = !!nextCursor;
  const rangeStart = events.length === 0 ? 0 : pageIndex * AUDIT_PAGE_SIZE + 1;
  const rangeEnd = pageIndex * AUDIT_PAGE_SIZE + events.length;

  const goFirst = () => setPageIndex(0);
  const goPrev = () => setPageIndex((i) => Math.max(0, i - 1));
  const goNext = () => {
    if (!nextCursor) return;
    setCursorStack((stack) => (pageIndex === stack.length - 1 ? [...stack, nextCursor] : stack));
    setPageIndex((i) => i + 1);
  };

  // --- Actions -------------------------------------------------------------------------------

  const choosePreset = (v: AuditPreset) => {
    setPreset(v);
    if (v !== 'custom') setLastRolling(v);
  };

  const setCustomDate = (which: 'from' | 'to', value: string) => {
    const nextFrom = which === 'from' ? value : customFrom;
    const nextTo = which === 'to' ? value : customTo;
    if (which === 'from') setCustomFrom(value);
    else setCustomTo(value);
    // Only a complete, ordered pair is applied; otherwise the previous range stays applied.
    if (customRangeStatus(nextFrom, nextTo) === 'ok') setAppliedCustom({ from: nextFrom, to: nextTo });
  };
  const customStatus = customRangeStatus(customFrom, customTo);

  const toggleCategory = (cat: string) =>
    setCategories((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));

  const toggleStatus = (status: string) => {
    const next = toggleStatusSelection({ eventTypes, statuses }, status);
    setEventTypes(next.eventTypes);
    setStatuses(next.statuses);
  };

  const toggleEventType = (eventType: string) => {
    const next = toggleEventTypeSelection({ eventTypes, statuses }, eventType);
    setEventTypes(next.eventTypes);
    setStatuses(next.statuses);
  };

  const toggleActor = (key: string) => setActor((prev) => (prev === key ? null : key));

  const clearDeposit = () => {
    setDepositId(null);
    setDepositLabel(null);
  };

  /** Deposit drill-down: this deposit's full history (all time, every event type/person). */
  const showDepositHistory = (event: AuditEventEnvelope) => {
    if (event.entityType !== 'deposit' || !event.entityId) return;
    setDepositId(event.entityId);
    setDepositLabel(event.entityLabel ?? null);
    setEventTypes([]);
    setStatuses([]);
    setActor(null);
    setCategories([]);
    setQInput('');
    setDebouncedQ('');
    setIp('');
    choosePreset('all');
    setSelected(null);
  };

  /** Clears every filter but keeps the date range (an incomplete custom range falls back to 24h). */
  const clearFilters = () => {
    setEventTypes([]);
    setStatuses([]);
    setActor(null);
    setClientFilter(null);
    setCategories([]);
    setQInput('');
    setDebouncedQ('');
    setIp('');
    clearDeposit();
    if (preset === 'custom' && (!customFrom || !customTo)) choosePreset('1d');
  };

  const toggleBurst = useCallback((key: string) => {
    setExpandedBursts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleRefresh = () => {
    const rerolled = resolveRange({ preset, lastRolling, appliedCustom }).from;
    if (rerolled !== filters.from) {
      // The rolling window has moved on since the query was built: re-anchor it. The new
      // query keys make BOTH the page and the facets fetch.
      setRefreshedAt(Date.now());
    } else {
      void pageQuery.refetch();
      void facetsQuery.refetch();
    }
  };

  const copyIp = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('IP copied');
    } catch {
      /* clipboard unavailable */
    }
  };


  const handleExport = async () => {
    try {
      setIsExporting(true);
      const blob = await auditEventsService.exportCsv(filters, browserTimeZone());
      // The server appends a `"TRUNCATED…` line when its row cap is hit — only the tail matters.
      let truncated = false;
      try {
        truncated = csvIsTruncated(await blob.slice(Math.max(0, blob.size - 8192)).text());
      } catch {
        /* unreadable tail — treat as complete */
      }
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `audit_events_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
      if (truncated) {
        toast('Export hit the 50,000-row limit, so it is incomplete. Narrow the date range or filters and export again.', {
          icon: <i className="fas fa-triangle-exclamation text-amber-500" aria-hidden="true" />,
          duration: 10000,
        });
      } else {
        toast.success('Audit log exported');
      }
    } catch {
      toast.error('Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  // --- Chips ---------------------------------------------------------------------------------

  const actorName = useMemo(() => {
    if (!actor) return null;
    const entry = (facets?.byActor ?? []).find((a) => actorFacetKey(a) === actor);
    return entry ? actorFacetName(entry) : actorKeyFallbackLabel(actor);
  }, [actor, facets]);

  const depositChipLabel =
    depositLabel ??
    events.find((e) => e.entityType === 'deposit' && e.entityId === depositId && e.entityLabel)?.entityLabel ??
    (depositId ? shortId(depositId) : '');

  const chips: FilterChip[] = [
    ...eventTypes.map((t) => ({ key: `type:${t}`, label: eventTypeLabel(t), title: t, onRemove: () => toggleEventType(t) })),
    ...statuses.map((s) => ({ key: `status:${s}`, label: `Moved to ${statusLabel(s)}`, title: s, onRemove: () => toggleStatus(s) })),
    ...(actor && actorName ? [{ key: 'actor', label: actorName, onRemove: () => setActor(null) }] : []),
    ...categories.map((cat) => ({ key: `cat:${cat}`, label: categoryMeta(cat).label, onRemove: () => toggleCategory(cat) })),
    ...(depositId
      ? [{ key: 'deposit', label: `Deposit: ${depositChipLabel}`, title: 'Showing this deposit’s full history', onRemove: clearDeposit }]
      : []),
  ];

  return (
    <div data-audit-explorer={AUDIT_EXPLORER_MARKER} className="relative flex h-full flex-col overflow-hidden">
      {/* Header: title/subtitle LEFT — prominent centered search — controls RIGHT */}
      <div className="flex items-center gap-3 border-b bg-gray-50 px-4 py-2">
        <div className="flex min-w-0 shrink-0 items-center gap-2">
          <i className="fas fa-shield-halved text-blue-600" aria-hidden="true" />
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900">
              {scope === 'system' ? 'Audit Console' : 'Audit Trail'}
            </h2>
            <p className="truncate text-[10px] text-gray-500">
              {scope === 'system' ? 'All clients' : clientName || 'This client'} •{' '}
              {summary ?? (facetsQuery.isError ? 'totals unavailable' : 'Loading…')}
            </p>
          </div>
        </div>

        {/* Prominent centered search */}
        <div className="flex flex-1 justify-center px-2">
          <div className="relative w-full max-w-md">
            <input
              ref={searchRef}
              type="text"
              placeholder="Search by person, action, or entity…"
              aria-label="Search audit events"
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-9 pr-3 text-sm placeholder-gray-400 shadow-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400" aria-hidden="true" />
            {qInput && (
              <button
                type="button"
                onClick={() => setQInput('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
                title="Clear search"
                aria-label="Clear search"
              >
                <i className="fas fa-times-circle text-xs" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <select
            value={preset}
            onChange={(e) => choosePreset(e.target.value as AuditPreset)}
            className="rounded border border-gray-300 bg-white px-2 py-1 text-xs focus:outline-none"
            title="Date range"
            aria-label="Date range"
          >
            {TIME_PRESETS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleExport}
            disabled={isExporting || !enabled}
            className="flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-50"
            title="Export current view to CSV"
            aria-label="Export current view to CSV"
          >
            <i className="fas fa-download text-[10px]" aria-hidden="true" />
            CSV
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="ml-1 text-gray-400 hover:text-gray-600"
              title="Close"
              aria-label="Close audit console"
            >
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Custom range inputs — only when the custom preset is selected */}
      {preset === 'custom' && (
        <div className="flex items-center gap-2 border-b bg-white px-4 py-1.5 text-xs">
          <i className="fas fa-calendar-day text-[10px] text-gray-400" aria-hidden="true" />
          <label className="text-gray-500" htmlFor="audit-custom-from">
            From
          </label>
          <input
            id="audit-custom-from"
            type="date"
            value={customFrom}
            max={customTo || undefined}
            onChange={(e) => setCustomDate('from', e.target.value)}
            className="rounded border border-gray-300 bg-white px-2 py-1 focus:outline-none"
          />
          <label className="text-gray-500" htmlFor="audit-custom-to">
            To
          </label>
          <input
            id="audit-custom-to"
            type="date"
            value={customTo}
            min={customFrom || undefined}
            onChange={(e) => setCustomDate('to', e.target.value)}
            className="rounded border border-gray-300 bg-white px-2 py-1 focus:outline-none"
          />
          {customStatus !== 'ok' && (
            <span className="text-[11px] text-amber-600" role="status">
              <i className="fas fa-circle-info mr-1 text-[10px]" aria-hidden="true" />
              {customStatus === 'incomplete' ? 'Pick both dates' : 'The start date must be on or before the end date'}
            </span>
          )}
        </div>
      )}

      {/* Distribution stats (deposits-page style): Statuses/Categories + Top people, both clickable */}
      <div className="border-b bg-white px-4 py-1.5">
        {facetsQuery.isError && (
          <div className="flex items-center gap-1 pb-0.5 text-[11px] text-red-600" role="alert">
            <i className="fas fa-triangle-exclamation text-[10px]" aria-hidden="true" />
            Couldn&apos;t load totals —
            <button
              type="button"
              onClick={() => void facetsQuery.refetch()}
              className="font-medium underline hover:text-red-800"
              aria-label="Retry loading totals"
            >
              retry
            </button>
          </div>
        )}
        {(!facetsQuery.isError || facets) && (
          <AuditDistributionBar
            facets={facets}
            loading={facetsQuery.isLoading}
            statusView={statusView}
            activeCategories={categories}
            activeStatuses={statuses}
            activeActor={actor}
            onToggleCategory={toggleCategory}
            onToggleStatus={toggleStatus}
            onToggleActor={toggleActor}
          />
        )}
      </div>

      {/* Filter bar (client / event type / status / person / IP) + chips for every active filter. */}
      <AuditFilterBar
        facets={facets}
        clients={scope === 'system' ? availableClients : []}
        clientId={clientFilter}
        eventTypes={eventTypes}
        statuses={statuses}
        actor={actor}
        ip={ip}
        onClientChange={setClientFilter}
        onToggleEventType={toggleEventType}
        onClearEventTypes={() => setEventTypes([])}
        onToggleStatus={toggleStatus}
        onClearStatuses={() => setStatuses([])}
        onActorChange={setActor}
        onIpChange={setIp}
        onClear={clearFilters}
        activeFilterCount={activeFilterCount}
        chips={chips}
      />

      {/* Content — the drawer floats over this region and does NOT scroll with the table.
          The scroll lives on the inner div; the drawer is absolute-positioned relative to
          this non-scrolling wrapper, so it stays fixed no matter where the list is scrolled. */}
      <div className="relative flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto">
          {!enabled ? (
            <StateBlock icon="fa-user-lock" text="Select a client to load audit history" />
          ) : unauthorized ? (
            <StateBlock icon="fa-lock" text="Audit access requires admin permissions" tone="muted" />
          ) : pageQuery.isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner />
            </div>
          ) : pageQuery.error ? (
            <div className="m-4 rounded border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              <i className="fas fa-triangle-exclamation mr-1" aria-hidden="true" /> Failed to load audit events. Please try again.
            </div>
          ) : events.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-sm text-gray-400">
              <i className="fas fa-inbox text-2xl" aria-hidden="true" />
              <span>No events match these filters</span>
              {activeFilterCount > 0 && (
                <button type="button" onClick={clearFilters} className="text-xs text-blue-500 hover:underline">
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-gray-50 text-left text-[10px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Time</th>
                  <th className="px-3 py-2 font-medium">Event</th>
                  <th className="px-3 py-2 font-medium">Actor</th>
                  <th className="w-24 px-3 py-2 font-medium">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {listItems.map((item) =>
                  item.kind === 'single' ? (
                    <AuditEventRow
                      key={item.key}
                      event={item.event}
                      selected={selected?.id === item.event.id}
                      onSelect={setSelected}
                      onCopyIp={copyIp}
                      onDrillDeposit={showDepositHistory}
                    />
                  ) : (
                    <AuditBurstRow
                      key={item.key}
                      events={item.events}
                      expanded={expandedBursts.has(item.key)}
                      onToggle={() => toggleBurst(item.key)}
                      selectedId={selected?.id ?? null}
                      onSelect={setSelected}
                      onCopyIp={copyIp}
                      onDrillDeposit={showDepositHistory}
                    />
                  )
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail drawer floats over the visible content (its own body scrolls internally) */}
        <AuditDetailDrawer event={selected} onClose={() => setSelected(null)} onViewDepositHistory={showDepositHistory} />
      </div>

      {/* Footer: range summary + compact keyset pager */}
      <div className="flex items-center justify-between gap-2 border-t bg-gray-50 px-4 py-2 text-[11px]">
        <span className="truncate text-gray-500">
          {events.length === 0 ? (
            total === 0 ? 'No events' : ''
          ) : (
            <>
              Showing <span className="font-semibold text-gray-700">{rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()}</span>
              {total != null && (
                <>
                  {' '}
                  of <span className="font-semibold text-gray-700">{total.toLocaleString()}</span>
                </>
              )}
            </>
          )}
        </span>

        <div className="flex items-center gap-1.5">
          {(pageQuery.isFetching || facetsQuery.isFetching) && <Spinner size="sm" />}
          <button
            type="button"
            onClick={handleRefresh}
            className="flex h-6 w-6 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-200 hover:text-gray-600"
            title="Refresh"
            aria-label="Refresh events and totals"
          >
            <i className="fas fa-rotate-right text-[11px]" aria-hidden="true" />
          </button>
          <div className="flex items-center gap-0.5 rounded-full border border-gray-200 bg-white p-0.5 shadow-sm">
            <PagerButton icon="fa-angles-left" title="First page" disabled={!hasPrev} onClick={goFirst} />
            <PagerButton icon="fa-angle-left" title="Previous page" disabled={!hasPrev} onClick={goPrev} />
            <span className="select-none px-2 text-[11px] font-medium tabular-nums text-gray-600">
              {(pageIndex + 1).toLocaleString()}
              {totalPages != null && (
                <>
                  {' '}
                  <span className="text-gray-300">/</span> {totalPages.toLocaleString()}
                </>
              )}
            </span>
            <PagerButton icon="fa-angle-right" title="Next page" disabled={!hasNext} onClick={goNext} />
          </div>
        </div>
      </div>
    </div>
  );
};

/** Compact round pager button. */
const PagerButton: React.FC<{ icon: string; title: string; disabled?: boolean; onClick: () => void }> = ({
  icon,
  title,
  disabled,
  onClick,
}) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    disabled={disabled}
    className="flex h-6 w-6 items-center justify-center rounded-full text-gray-500 transition hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-500"
  >
    <i className={`fas ${icon} text-[11px]`} aria-hidden="true" />
  </button>
);

const StateBlock: React.FC<{ icon: string; text: string; tone?: 'muted' }> = ({ icon, text, tone }) => (
  <div className={`flex flex-col items-center justify-center gap-2 py-12 text-sm ${tone === 'muted' ? 'text-gray-400' : 'text-gray-500'}`}>
    <i className={`fas ${icon} text-2xl`} aria-hidden="true" />
    <span>{text}</span>
  </div>
);

export default AuditExplorer;
